import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import MerchantApkDownload from "../common/MerchantApkDownload";
import { canOpenBackofficeOnCurrentHost } from "../../services/local/runtimeHost";
import { getMerchantEntryContinuation } from "../../services/local/merchantEntryNavigation";

const normalizeAvailableModes = (branch) =>
  Array.isArray(branch?.availableModes) ? branch.availableModes : ["admin"];

function SeleccionAcceso() {
  const {
    session,
    currentCompany,
    availableBranches,
    isAdminGeneral,
    isPlatformBackofficeAdmin,
    isCreatorMode,
    loadingWorkspace,
    enterGlobalAccess,
    enterBranchAccess,
    enterBranchAdminAccess,
    enterBranchWaiterAccess,
    enterBranchKitchenAccess,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const merchantContinuation = getMerchantEntryContinuation(location);
  const [selectedBranchId, setSelectedBranchId] = useState(
    availableBranches[0]?.id || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");

  const getModeMeta = useCallback((mode) => {
    if (mode === "mozo") {
      return {
        key: "mozo",
        pill: "Mozo",
        label: "Entrar como mozo",
        description: "Toma pedidos y trabaja con el salon.",
        targetPath: "/mozo",
      };
    }

    if (mode === "cocina") {
      return {
        key: "cocina",
        pill: "Cocina",
        label: "Entrar a cocina",
        description: "Abre la pantalla operativa de cocina.",
        targetPath: "/cocina",
      };
    }

    return {
      key: "admin",
      pill: "Admin",
      label: "Entrar como admin",
      description: "Abre el panel operativo de la sucursal.",
      targetPath: merchantContinuation || "/app",
    };
  }, [merchantContinuation]);

  const branchCards = useMemo(
    () =>
      availableBranches.map((branch) => ({
        ...branch,
        summary: [branch.address, branch.phone].filter(Boolean).join(" · "),
        availableModes: normalizeAvailableModes(branch),
      })),
    [availableBranches]
  );

  const selectedBranch = useMemo(
    () => branchCards.find((branch) => branch.id === selectedBranchId) || null,
    [branchCards, selectedBranchId]
  );

  const selectedBranchModeOptions = useMemo(() => {
    if (!selectedBranch) {
      return [];
    }

    return selectedBranch.availableModes.map(getModeMeta);
  }, [getModeMeta, selectedBranch]);

  useEffect(() => {
    if (!selectedBranchId && availableBranches[0]?.id) {
      setSelectedBranchId(availableBranches[0].id);
    }
  }, [availableBranches, selectedBranchId]);

  const handleEnterGlobal = useCallback(() => {
    setSubmitting(true);
    setLocalError("");
    enterGlobalAccess();
    navigate(merchantContinuation || "/app", { replace: true });
  }, [enterGlobalAccess, merchantContinuation, navigate]);

  const handleEnterBranch = useCallback(
    (branchId, branchMode = "admin") => {
      setSubmitting(true);
      setLocalError("");

      let applied = false;

      if (branchMode === "mozo") {
        applied = enterBranchWaiterAccess(branchId);
      } else if (branchMode === "cocina") {
        applied = enterBranchKitchenAccess(branchId);
      } else if (branchMode === "admin") {
        applied = enterBranchAdminAccess(branchId);
      } else {
        applied = enterBranchAccess(branchId, branchMode);
      }

      if (!applied) {
        setSubmitting(false);
        setLocalError("No pudimos activar esa sucursal. Probemos otra vez.");
        return;
      }

      navigate(getModeMeta(branchMode).targetPath, { replace: true });
    },
    [
      enterBranchAccess,
      enterBranchAdminAccess,
      enterBranchKitchenAccess,
      enterBranchWaiterAccess,
      getModeMeta,
      navigate,
    ]
  );

  useEffect(() => {
    if (
      !session ||
      isCreatorMode ||
      isAdminGeneral ||
      loadingWorkspace ||
      submitting ||
      availableBranches.length !== 1
    ) {
      return;
    }

    const onlyBranch = availableBranches[0];
    const onlyBranchModes = normalizeAvailableModes(onlyBranch);

    if (!onlyBranch?.id || onlyBranchModes.length !== 1) {
      return;
    }

    handleEnterBranch(onlyBranch.id, onlyBranchModes[0]);
  }, [
    availableBranches,
    handleEnterBranch,
    isAdminGeneral,
    isCreatorMode,
    loadingWorkspace,
    session,
    submitting,
  ]);

  if (!session) {
    return <Navigate to="/app" replace />;
  }

  if (isCreatorMode) {
    return <Navigate to={merchantContinuation || "/app"} replace />;
  }

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <div style={styles.brand}>
          {merchantContinuation ? "We!Pedidos Comercios" : "MozzoApp"}
        </div>
        <h1 style={styles.title}>Elegi como queres entrar hoy</h1>
        <p style={styles.subtitle}>
          {isAdminGeneral
            ? `Tu cuenta pertenece a ${currentCompany?.name || "la empresa activa"}. Podes abrir el panel general o entrar a una sucursal puntual para trabajar con contexto acotado.`
            : `Tu cuenta tiene acceso a ${currentCompany?.name || "la empresa activa"}. Si en una sucursal trabajas con mas de un perfil, elegi el modo para esta sesion.`}
        </p>

        <MerchantApkDownload />

        {isAdminGeneral && (
          <div style={styles.modeGrid}>
            <button
              type="button"
              style={{ ...styles.modeCard, ...styles.modeCardPrimary }}
              onClick={handleEnterGlobal}
              disabled={submitting || loadingWorkspace}
            >
              <div style={styles.modeEyebrow}>Acceso total</div>
              <div style={styles.modeTitle}>Panel general de empresa</div>
              <div style={styles.modeText}>
                Entras con vision completa, configuracion general y selector libre de sucursales.
              </div>
            </button>

            <div style={styles.modeCard}>
              <div style={styles.modeEyebrow}>Acceso por sucursal</div>
              <div style={styles.modeTitle}>Trabajar en una sucursal</div>
              <div style={styles.modeText}>
                Ideal para dejar una sesion operativa en un local puntual, sin abrir el panel global.
              </div>
              <div style={styles.compactSelector}>
                <select
                  value={selectedBranchId}
                  onChange={(event) => setSelectedBranchId(event.target.value)}
                  style={styles.select}
                  disabled={submitting || loadingWorkspace || branchCards.length === 0}
                >
                  {branchCards.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <div style={styles.compactButtonRow}>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    onClick={() => handleEnterBranch(selectedBranchId, "admin")}
                    disabled={
                      submitting || loadingWorkspace || !selectedBranchId || branchCards.length === 0
                    }
                  >
                    Entrar a sucursal
                  </button>
                  {selectedBranchModeOptions.some((option) => option.key === "cocina") ? (
                    <button
                      type="button"
                      style={styles.secondaryButtonMuted}
                      onClick={() => handleEnterBranch(selectedBranchId, "cocina")}
                      disabled={
                        submitting ||
                        loadingWorkspace ||
                        !selectedBranchId ||
                        branchCards.length === 0
                      }
                    >
                      Entrar a cocina
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {isPlatformBackofficeAdmin && canOpenBackofficeOnCurrentHost() ? (
              <button
                type="button"
                style={styles.modeCard}
                onClick={() => navigate("/backoffice", { replace: true })}
                disabled={submitting || loadingWorkspace}
              >
                <div style={styles.modeEyebrow}>Interno MozzoApp</div>
                <div style={styles.modeTitle}>Backoffice de plataforma</div>
                <div style={styles.modeText}>
                  Abre el panel interno para billetera MZ, reglas comerciales y seguimiento de restaurantes.
                </div>
              </button>
            ) : null}
          </div>
        )}

        <div style={styles.branchSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {isAdminGeneral ? "Sucursales disponibles" : "Sucursales asignadas"}
            </h2>
            <p style={styles.sectionText}>
              {isAdminGeneral
                ? "Tambien podes entrar directo a cualquier sucursal desde esta lista."
                : "Cada tarjeta muestra los modos habilitados para esa sucursal. Asi una misma persona puede entrar como admin, mozo o cocina segun el turno."}
            </p>
          </div>

          <div style={styles.branchGrid}>
            {branchCards.map((branch) => (
              <div
                key={branch.id}
                style={{
                  ...styles.branchCard,
                  ...(selectedBranchId === branch.id ? styles.branchCardSelected : {}),
                }}
                onClick={() => setSelectedBranchId(branch.id)}
              >
                <div style={styles.branchName}>{branch.name}</div>
                <div style={styles.branchSummary}>
                  {branch.summary || "Sucursal operativa"}
                </div>
                <div style={styles.modePills}>
                  {branch.availableModes.map((mode) => {
                    const modeMeta = getModeMeta(mode);

                    return (
                      <span key={`${branch.id}-${mode}`} style={styles.modePill}>
                        {modeMeta.pill}
                      </span>
                    );
                  })}
                </div>
                <div style={styles.branchActionGroup}>
                  {branch.availableModes.map((mode) => {
                    const modeMeta = getModeMeta(mode);

                    return (
                      <button
                        key={`${branch.id}-${mode}-action`}
                        type="button"
                        style={
                          modeMeta.key === "admin"
                            ? styles.branchPrimaryAction
                            : styles.branchSecondaryAction
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedBranchId(branch.id);
                          handleEnterBranch(branch.id, mode);
                        }}
                        disabled={submitting || loadingWorkspace}
                      >
                        {modeMeta.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {localError && <div style={styles.errorBanner}>{localError}</div>}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 20px",
    background:
      "linear-gradient(160deg, rgba(248,250,252,1) 0%, rgba(241,245,249,1) 55%, rgba(226,232,240,1) 100%)",
  },
  panel: {
    width: "100%",
    maxWidth: "1080px",
    backgroundColor: "#ffffff",
    borderRadius: "22px",
    padding: "32px",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    boxShadow: "0 22px 60px rgba(15, 23, 42, 0.1)",
    display: "grid",
    gap: "24px",
  },
  brand: {
    display: "inline-flex",
    width: "fit-content",
    padding: "8px 14px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#4338ca",
    fontWeight: 700,
  },
  title: {
    margin: 0,
    color: "#0f172a",
  },
  subtitle: {
    margin: "-12px 0 0",
    color: "#475569",
    lineHeight: 1.6,
    maxWidth: "860px",
  },
  modeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
  },
  modeCard: {
    border: "1px solid #dbe3ee",
    borderRadius: "18px",
    padding: "22px",
    backgroundColor: "#f8fafc",
    textAlign: "left",
    display: "grid",
    gap: "10px",
  },
  modeCardPrimary: {
    backgroundColor: "#0f172a",
    color: "#ffffff",
    borderColor: "#0f172a",
    cursor: "pointer",
  },
  modeEyebrow: {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    opacity: 0.75,
  },
  modeTitle: {
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  modeText: {
    color: "inherit",
    opacity: 0.9,
    lineHeight: 1.6,
  },
  compactSelector: {
    display: "grid",
    gap: "12px",
    marginTop: "6px",
  },
  compactButtonRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  select: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    padding: "12px 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontSize: "15px",
  },
  secondaryButton: {
    border: "none",
    borderRadius: "12px",
    padding: "12px 14px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButtonMuted: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  branchSection: {
    display: "grid",
    gap: "16px",
  },
  sectionHeader: {
    display: "grid",
    gap: "6px",
  },
  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "20px",
  },
  sectionText: {
    margin: 0,
    color: "#64748b",
    lineHeight: 1.6,
  },
  branchGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "14px",
  },
  branchCard: {
    textAlign: "left",
    border: "1px solid #dbe3ee",
    borderRadius: "16px",
    padding: "18px",
    backgroundColor: "#ffffff",
    display: "grid",
    gap: "10px",
    cursor: "pointer",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
  },
  branchCardSelected: {
    borderColor: "#2563eb",
    boxShadow: "0 12px 28px rgba(37, 99, 235, 0.12)",
    transform: "translateY(-1px)",
  },
  branchName: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#0f172a",
  },
  branchSummary: {
    color: "#64748b",
    lineHeight: 1.5,
    minHeight: "40px",
  },
  modePills: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  modePill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: "28px",
    padding: "0 10px",
    borderRadius: "999px",
    backgroundColor: "#eef2ff",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 800,
  },
  branchActionGroup: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginTop: "4px",
  },
  branchPrimaryAction: {
    border: "none",
    borderRadius: "12px",
    padding: "11px 14px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  branchSecondaryAction: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "11px 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  errorBanner: {
    borderRadius: "14px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#be123c",
    padding: "14px 16px",
  },
};

export default SeleccionAcceso;
